/* eslint-disable react/prop-types */

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    DndContext,
    PointerSensor,
    TouchSensor,
    closestCenter,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from "@dnd-kit/core";

import { CSS } from "@dnd-kit/utilities";

import SaveTasks from "../../../services/general/TaskCrud.api";
import CreateTask from "./create-task";
import { useParams } from "react-router-dom";
import UpdateTask from "./update-task";
import DeleteBoard from "./delete-board";

import {
    CButton,
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CModalFooter,
    CFormInput,
    CFormLabel,
    CSpinner,
} from "@coreui/react";

import {
    ArrowLeft,
    Search,
    Calendar,
    Trash2,
    X,
} from "lucide-react";

import HRMFilterIcon from "../../../components/shared/HRMFilterIcon";
import Select from "react-select";
import { hasIncompleteTaskSubtasks } from './taskUtility';
import toast from "react-hot-toast";
import { toastConfig } from "../../../constants/other.config";

import SaveBoard from "../../../services/general/BoardCrud.api";

import {
    findKanbanStatusColumn,
    normalizeKanbanColumns,
} from "./kanbanStatusUtils";

import {
    formatValueToMMDDYYYY,
    parseUSDateValue,
} from "../../../components/shared/customDateFormatUtils";

import "../dashboard.css";


const KANBAN_PRIMARY_BLUE = "#1A76D3";


/* =========================================================
   RESPONSE HELPERS
========================================================= */

const parseTaskRows = (response) => {
    try {
        const parsed =
            typeof response?.d === "string"
                ? JSON.parse(response.d)
                : response?.d || response;

        if (Array.isArray(parsed?.data)) return parsed.data;
        if (Array.isArray(parsed?.Data)) return parsed.Data;
        if (Array.isArray(parsed)) return parsed;
    } catch (error) {
        console.error(
            "TempKanban task response parse error:",
            error
        );
    }

    return [];
};


/* =========================================================
   SELECT STYLES
========================================================= */

const assigneeSelectStyles = {
    control: (base) => ({
        ...base,
        minHeight: "44px",
        borderRadius: "12px",
        borderColor: "var(--ui-border-color, #D1D5DB)",
        backgroundColor: "var(--ui-bg-filter, #ffffff)",
        boxShadow: "none",
        cursor: "pointer",

        "&:hover": {
            borderColor: "var(--ui-border-color, #D1D5DB)",
        },
    }),

    valueContainer: (base) => ({
        ...base,
        padding: "2px 12px",
    }),

    singleValue: (base) => ({
        ...base,
        color: "var(--ui-text-primary, #0F2A55)",
        fontWeight: 600,
    }),

    multiValue: (base) => ({ ...base, backgroundColor: "rgba(43, 124, 211, 0.2)", borderRadius: "6px" }),
    multiValueLabel: (base) => ({ ...base, color: "var(--ui-text-primary, #ffffff)", fontSize: "12px" }),
    multiValueRemove: (base) => ({ ...base, color: "var(--ui-text-secondary, #8a93a8)", ":hover": { color: "#ffffff" } }),

    placeholder: (base) => ({
        ...base,
        color: "var(--ui-text-secondary, #667085)",
    }),

    indicatorSeparator: () => ({
        display: "none",
    }),

    dropdownIndicator: (base) => ({
        ...base,
        color: KANBAN_PRIMARY_BLUE,

        "&:hover": {
            color: KANBAN_PRIMARY_BLUE,
        },
    }),

    menu: (base) => ({
        ...base,
        marginTop: "8px",
        border: "1px solid var(--ui-border-color, #E5E7EB)",
        backgroundColor: "var(--ui-bg-filter, #ffffff)",
        borderRadius: "12px",
        boxShadow: "0 18px 36px rgba(15, 23, 42, 0.14)",
        overflow: "hidden",
        zIndex: 70,
    }),

    menuList: (base) => ({
        ...base,
        padding: "6px 0",
        maxHeight: "220px",
    }),

    option: (base, state) => ({
        ...base,

        backgroundColor: state.isSelected
            ? "var(--ui-color-primary-soft)"
            : state.isFocused
                ? "var(--ui-bg-hover)"
                : "var(--ui-bg-filter)",

        color: "var(--ui-text-primary)",
        cursor: "pointer",
        fontWeight: 600,
        padding: "12px 16px",

        ":active": {
            backgroundColor: "var(--ui-color-primary-soft)",
        },
    }),
};


/* =========================================================
   STATUS HELPERS
========================================================= */

const normalizeStatusName = (value = "") =>
    String(value)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");


const isArchiveStatus = (statusColumnOrName) => {
    const statusName =
        typeof statusColumnOrName === "object"
            ? statusColumnOrName?.headerText ||
            statusColumnOrName?.keyField
            : statusColumnOrName;

    const normalized = normalizeStatusName(statusName);

    return (
        normalized === "archived" ||
        normalized === "archive" ||
        normalized === "achived" ||
        normalized === "achive"
    );
};


/* =========================================================
   ASSIGNEE HELPERS
========================================================= */

const normalizeTaskAssignees = (assignee) => {
    const extract = (value) => {
        if (
            value === null ||
            value === undefined ||
            value === false
        ) {
            return [];
        }

        if (typeof value === "string") {
            const trimmed = value.trim();

            if (!trimmed) return [];

            if (
                (trimmed.startsWith("[") &&
                    trimmed.endsWith("]")) ||
                (trimmed.startsWith("{") &&
                    trimmed.endsWith("}"))
            ) {
                try {
                    const json = JSON.parse(trimmed);

                    return normalizeTaskAssignees(json);
                } catch {
                    return [trimmed];
                }
            }

            return [trimmed];
        }

        if (Array.isArray(value)) {
            return value.flatMap(extract);
        }

        if (typeof value === "object") {
            const candidates = [
                value.label,
                value.Username,
                value.username,
                value.name,
                value.user,
                value.UserId,
                value.userId,
                value.id,
                value.ref_id,
                value.value,
            ].filter((v) => v !== null && v !== undefined && v !== "");

            if (candidates.length) return candidates.map((v) => String(v).trim());

            return Object.values(value).flatMap(extract);
        }

        return [String(value).trim()];
    };

    return Array.from(
        new Set(extract(assignee))
    ).filter(Boolean);
};


/* =========================================================
   DRAGGABLE TASK CARD
========================================================= */

const DraggableTaskCard = ({
    task,
    onOpen,
    onDelete,
}) => {
    const taskId = String(
        task?.TaskId ??
        task?.Id ??
        task?.ID ??
        task?.taskId ??
        task?.id ??
        ""
    );

    const isArchivedTask = Boolean(
        task?.isArchived ||
        task?.IsArchived ||
        isArchiveStatus(
            task?.status ||
            task?.Status ||
            task?.statusId ||
            task?.StatusId ||
            ""
        )
    );

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging,
    } = useDraggable({
        id: `task-${taskId}`,
        disabled: isArchivedTask,
        data: {
            type: "task",
            task,
            taskId,
            statusId:
                task?.statusId ??
                task?.StatusId ??
                "",
        },
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        zIndex: isDragging ? 1000 : "auto",
        opacity: isDragging ? 0.45 : 1,
        position: "relative",
        cursor: isArchivedTask
            ? "default"
            : isDragging
                ? "grabbing"
                : "grab",
    };

    const linkedMedication =
        Array.isArray(task?.linkedMedications)
            ? task.linkedMedications[0]
            : task?.linkedMedications;

    const linkedPatientName = String(
        linkedMedication?.patientName ||
        linkedMedication?.PatientName ||
        ""
    ).trim();

    const linkedMedicationName = String(
        linkedMedication?.name ||
        linkedMedication?.medicationName ||
        task?.medicationName ||
        task?.MedicationName ||
        ""
    ).trim();

    const rawTitle =
        task?.title ||
        task?.Title ||
        "";

    const title =
        linkedPatientName && linkedMedicationName
            ? `${linkedPatientName} - ${linkedMedicationName}`
            : rawTitle;

    const dueRaw =
        task?.dueDate ||
        task?.DueDate ||
        task?.Due ||
        task?.due ||
        null;

    const dueDate = parseUSDateValue(dueRaw);

    const formattedDue =
        formatValueToMMDDYYYY(dueRaw);

    const priority =
        task?.priority ||
        task?.Priority ||
        "";

    const priorityColor =
        priority === "High"
            ? "rgb(231, 0, 0)"
            : priority === "Low"
                ? "#16a34a"
                : "#2563eb";

    const subtasks =
        task?.subTasks ||
        task?.subtask ||
        task?.SubTasks ||
        task?.subtasks ||
        [];

    const totalSub =
        Array.isArray(subtasks)
            ? subtasks.length
            : 0;

    const completedSub =
        Array.isArray(subtasks)
            ? subtasks.filter(
                (s) =>
                    s?.completed ||
                    s?.isCompleted ||
                    s?.done ||
                    s?.checked ||
                    s?.Status === "Done" ||
                    s?.status === "done"
            ).length
            : 0;

    const handleCardClick = (event) => {
        /*
         * Don't open the task if the user is clicking
         * the delete button.
         */
        if (
            event.target.closest?.(
                ".tempkanban-delete-task-btn"
            )
        ) {
            return;
        }

        onOpen(task);
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="tempkanban-draggable-wrapper"
        >
            <div
                className="task-board-card p-2 rounded-lg"
                style={{
                    position: "relative",
                    cursor: isArchivedTask
                        ? "default"
                        : isDragging
                            ? "grabbing"
                            : "grab",
                    userSelect: "none",
                }}
                onClick={handleCardClick}
            >
                <button
                    type="button"
                    disabled={isArchivedTask}
                    className="tempkanban-delete-task-btn"
                    onPointerDown={(event) => {
                        event.stopPropagation();
                    }}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();

                        if (isArchivedTask) return;

                        onDelete(task);
                    }}
                    title={
                        isArchivedTask
                            ? "Delete is disabled for archived tasks"
                            : "Delete task"
                    }
                    style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        border: "none",
                        background: "transparent",
                        padding: 2,
                        color: isArchivedTask
                            ? "#9CA3AF"
                            : "rgb(231, 0, 0)",
                        opacity: isArchivedTask
                            ? 0.6
                            : 1,
                        cursor: isArchivedTask
                            ? "not-allowed"
                            : "pointer",
                        zIndex: 2,
                    }}
                >
                    <Trash2 size={20} />
                </button>

                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 15,
                    }}
                >
                    <p
                        className="p-0 m-0"
                        style={{
                            fontWeight: 600,
                            letterSpacing: "-0.4px",
                            lineHeight: "20px",
                            fontSize: "16px",
                            paddingRight: "28px",
                        }}
                    >
                        {title}
                    </p>

                    {formattedDue && (
                        (() => {
                            const dueObj =
                                dueDate
                                    ? new Date(dueDate)
                                    : null;

                            const today =
                                new Date();

                            today.setHours(
                                0,
                                0,
                                0,
                                0
                            );

                            const dueCompare =
                                dueObj
                                    ? new Date(
                                        dueObj
                                    )
                                    : null;

                            if (dueCompare) {
                                dueCompare.setHours(
                                    0,
                                    0,
                                    0,
                                    0
                                );
                            }

                            const overdue =
                                dueCompare
                                    ? dueCompare <
                                    today
                                    : false;

                            return (
                                <div
                                    style={{
                                        display:
                                            "inline-flex",
                                        alignItems:
                                            "center",
                                        gap: 8,
                                        border:
                                            `1px solid ${overdue
                                                ? "rgb(199 22 22)"
                                                : "var(--ui-dashboard-border-strong)"
                                            }`,
                                        borderRadius: 8,
                                        padding:
                                            "4px 8px",
                                        background:
                                            "transparent",
                                        width:
                                            "fit-content",
                                        whiteSpace:
                                            "nowrap",
                                    }}
                                >
                                    <Calendar
                                        size={16}
                                        style={{
                                            color:
                                                overdue
                                                    ? "rgb(199 22 22)"
                                                    : "var(--ui-text-muted)",
                                        }}
                                    />

                                    <div
                                        style={{
                                            color:
                                                overdue
                                                    ? "rgb(199 22 22)"
                                                    : "var(--ui-text-muted)",
                                            fontSize:
                                                "16px",
                                            lineHeight:
                                                "20px",
                                        }}
                                    >
                                        {formattedDue}
                                    </div>
                                </div>
                            );
                        })()
                    )}

                    <div
                        style={{
                            color: priorityColor,
                            fontWeight: 700,
                            fontSize: "16px",
                            lineHeight: "20px",
                        }}
                    >
                        {priority}
                    </div>

                    {totalSub > 0 && (
                        <div
                            style={{
                                color: "#6b7280",
                                fontSize: "16px",
                                lineHeight: "20px",
                            }}
                        >
                            {`${completedSub}/${totalSub}`}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


/* =========================================================
   DROPPABLE KANBAN COLUMN
========================================================= */

const KanbanColumn = ({
    column,
    tasks,
    onOpenTask,
    onDeleteTask,
    activeTaskId,
}) => {
    const columnId = String(
        column?.keyField ?? ""
    );

    const {
        setNodeRef,
        isOver,
    } = useDroppable({
        id: `column-${columnId}`,
        data: {
            type: "column",
            statusId: columnId,
            status: column?.headerText || "",
            column,
        },
    });

    return (
        <div
            className="tempkanban-column"
            style={{
                width: "100%",
  minWidth: 0,
                display: "flex",
                flexDirection: "column",
                minHeight: "520px",
                borderRadius: "12px",
                background:
                    "var(--ui-bg-modal)",
                border:
                    isOver
                        ? `2px solid ${KANBAN_PRIMARY_BLUE}`
                        : "1px solid var(--ui-dashboard-border)",
                transition:
                    "border-color 0.15s ease, background 0.15s ease",
            }}
        >
            {/* COLUMN HEADER */}
            <div
                style={{
                    padding: "14px 14px 12px",
                    borderBottom:
                        "1px solid var(--ui-dashboard-border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    minHeight: "52px",
                }}
            >
                <div
                    style={{
                        color:
                            "var(--ui-text-primary)",
                        fontWeight: 700,
                        fontSize: "15px",
                        lineHeight: "20px",
                        overflow: "hidden",
                        textOverflow:
                            "ellipsis",
                        whiteSpace:
                            "nowrap",
                    }}
                    title={
                        column?.headerText
                    }
                >
                    {column?.headerText ||
                        "Untitled"}
                </div>

                <div
                    style={{
                        minWidth: "28px",
                        height: "28px",
                        borderRadius: "999px",
                        background:
                            "var(--ui-dashboard-surface-3)",
                        color:
                            "var(--ui-text-primary)",
                        display:
                            "inline-flex",
                        alignItems:
                            "center",
                        justifyContent:
                            "center",
                        fontSize: "12px",
                        fontWeight: 700,
                        padding: "0 7px",
                    }}
                >
                    {tasks.length}
                </div>
            </div>

            {/* DROP AREA */}
            <div
                ref={setNodeRef}
                className="tempkanban-column-drop-area"
                style={{
                    flex: 1,
                    padding: "10px",
                    minHeight: "460px",
                    background: isOver
                        ? "var(--ui-color-primary-soft)"
                        : "transparent",
                    transition:
                        "background 0.15s ease",
                }}
            >
                {tasks.length === 0 ? (
                    <div
                        style={{
                            minHeight:
                                "120px",
                            borderRadius: "10px",
                            border:
                                "1px dashed var(--ui-dashboard-border-strong)",
                            display:
                                "flex",
                            alignItems:
                                "center",
                            justifyContent:
                                "center",
                            textAlign:
                                "center",
                            padding:
                                "20px 12px",
                            color:
                                "var(--ui-text-muted)",
                            fontSize: "13px",
                        }}
                    >
                        {isOver
                            ? "Drop task here"
                            : "No tasks"}
                    </div>
                ) : (
                    <div
                        style={{
                            display:
                                "flex",
                            flexDirection:
                                "column",
                            gap: "10px",
                        }}
                    >
                        {tasks.map(
                            (task) => (
                                <DraggableTaskCard
                                    key={
                                        task.id
                                    }
                                    task={
                                        task
                                    }
                                    onOpen={
                                        onOpenTask
                                    }
                                    onDelete={
                                        onDeleteTask
                                    }
                                    activeTaskId={
                                        activeTaskId
                                    }
                                />
                            )
                        )}

                        {/* Additional empty drop space */}
                        <div
                            style={{
                                minHeight:
                                    "80px",
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};


/* =========================================================
   MAIN TEMP KANBAN
========================================================= */

const TempKanban = () => {
    const { id: boardId } =
        useParams();

    const [boardData, setBoardData] =
        useState(null);
    const [isBoardLoading, setIsBoardLoading] =
        useState(true);

    const [createTaskVisible, setCreateTaskVisible] =
        useState(false);

    const [updateTaskVisible, setUpdateTaskVisible] =
        useState(false);

    const [updateTaskId, setUpdateTaskId] =
        useState(null);

    const [deleteTaskId, setDeleteTaskId] =
        useState(null);

    const [deleteTaskTitle, setDeleteTaskTitle] =
        useState("");

    const [deleteVisible, setDeleteVisible] =
        useState(false);

    const [isDeletingTask, setIsDeletingTask] =
        useState(false);

    const [archiveConfirmVisible, setArchiveConfirmVisible] =
        useState(false);

    const [archiveCandidate, setArchiveCandidate] =
        useState(null);

    const [filters, setFilters] =
        useState({
            search: "",
            assignee: "",
        });

    const [showFilterDropdown, setShowFilterDropdown] =
        useState(false);

    const [activeDragTask, setActiveDragTask] =
        useState(null);

    const boardContainerRef =
        useRef(null);

    const filterRef = useRef(null);
    const prevUpdateTaskVisibleRef = useRef(false);

    useEffect(() => {
        function handleClickOutside(event) {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setShowFilterDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [filterRef]);
    /* =====================================================
       DND SENSORS
    ===================================================== */

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 6,
            },
        }),

        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 5,
            },
        })
    );


    /* =====================================================
       CURRENT USER
    ===================================================== */

    const getCurrentUserDetails = () => {
        try {
            const rawUser =
                localStorage.getItem(
                    "user"
                );

            const rawLogin =
                localStorage.getItem(
                    "userLoginWithCredentials"
                );

            const parsedUser =
                rawUser
                    ? JSON.parse(rawUser)
                    : {};

            const parsedLogin =
                rawLogin
                    ? JSON.parse(rawLogin)
                    : {};

            return {
                userId: String(
                    parsedUser?.Id ||
                    parsedUser?.UserID ||
                    parsedUser?.UserId ||
                    parsedUser?.id ||
                    ""
                ),

                username: String(
                    parsedUser?.Username ||
                    parsedUser?.username ||
                    parsedUser?.UserName ||
                    parsedUser?.Name ||
                    parsedUser?.name ||
                    parsedUser?.Email ||
                    parsedUser?.email ||
                    parsedLogin?.username ||
                    parsedLogin?.Username ||
                    parsedLogin?.phoneNo ||
                    ""
                ),
            };
        } catch (error) {
            console.error(
                "Failed to read current user details in TempKanban:",
                error
            );

            return {
                userId: "",
                username: "",
            };
        }
    };


    /* =====================================================
       NORMALIZED COLUMNS
    ===================================================== */

    const normalizedColumns =
        useMemo(
            () =>
                normalizeKanbanColumns(
                    boardData?.columns || []
                ),
            [boardData?.columns]
        );


    const kanbanMinWidth =0 ;


    /* =====================================================
       ASSIGNEES
    ===================================================== */

    const assigneeOptions =
        useMemo(() => {
            const uniqueAssignees =
                new Map();

            (boardData?.tasks || [])
                .forEach((task) => {
                    const assignees =
                        task.Assignee ||
                        task.assignee ||
                        task.assignedUsers ||
                        task.AssignedUsersJson ||
                        task.assignedUsersJson ||
                        [];

                    let normalized =
                        [];

                    try {
                        normalized =
                            typeof assignees ===
                                "string"
                                ? JSON.parse(
                                    assignees ||
                                    "[]"
                                )
                                : assignees;
                    } catch {
                        normalized =
                            [];
                    }

                    if (
                        !Array.isArray(
                            normalized
                        )
                    ) {
                        normalized = [
                            normalized,
                        ];
                    }

                    normalized.forEach(
                        (user) => {
                            if (!user)
                                return;

                            const userId =
                                user?.UserId ??
                                user?.userId ??
                                user?.value;

                            const label =
                                user?.label ??
                                user?.Username ??
                                user?.username ??
                                user?.value;

                            if (
                                !userId ||
                                !label
                            ) {
                                return;
                            }

                            uniqueAssignees.set(
                                String(
                                    userId
                                ),
                                {
                                    UserId:
                                        Number(
                                            userId
                                        ),
                                    value:
                                        label,
                                    label:
                                        label,
                                }
                            );
                        }
                    );
                });

            return Array.from(
                uniqueAssignees.values()
            ).sort((a, b) =>
                a.label.localeCompare(
                    b.label
                )
            );
        }, [boardData?.tasks]);


    const assigneeFilterOptions =
        useMemo(
            () =>
                assigneeOptions.map(
                    (assignee) => ({
                        value:
                            assignee.label,
                        label:
                            assignee.label,
                    })
                ),
            [assigneeOptions]
        );


    const selectedAssignees = useMemo(() => {
        const list = Array.isArray(filters.assignee) ? filters.assignee : (filters.assignee ? [filters.assignee] : []);
        return list.map((val) => assigneeFilterOptions.find((o) => String(o.value) === String(val)) || { value: val, label: val });
    }, [filters.assignee, assigneeFilterOptions]);


    /* =====================================================
       FILTER TASKS
    ===================================================== */

    const filteredTasks =
        useMemo(() => {
            const searchString =
                String(
                    filters.search || ""
                )
                    .trim()
                    .toLowerCase();

            const searchTerms =
                searchString
                    .split(/\s+/)
                    .filter(Boolean);

            const selAssignees = (Array.isArray(filters.assignee) ? filters.assignee : [filters.assignee]).filter(Boolean).map((s) => String(s).toLowerCase());

            const memberMap = new Map();
            (boardData?.members || []).forEach((m) => {
                const id = String(m?.ref_id ?? m?.value ?? m?.UserId ?? "").trim().toLowerCase();
                const name = String(m?.name ?? m?.label ?? m?.Username ?? "").trim().toLowerCase();
                if (id && name) {
                    memberMap.set(id, name);
                    memberMap.set(name, id);
                }
            });

            return (
                boardData?.tasks || []
            ).filter((task) => {
                const assignees =
                    normalizeTaskAssignees([
                        task.Assignee,
                        task.assignee,
                        task.assignedUsers,
                        task.AssignedUsersJson,
                        task.assignedUsersJson,
                    ]);

                const matchesAssignee =
                    selAssignees.length === 0
                        ? true
                        : assignees.some((value) => {
                              const v = String(value).trim().toLowerCase();
                              return selAssignees.some((sel) => sel === v || memberMap.get(sel) === v || memberMap.get(v) === sel);
                          });

                if (
                    !matchesAssignee
                ) {
                    return false;
                }

                if (
                    searchTerms.length ===
                    0
                ) {
                    return true;
                }

                const tags =
                    Array.isArray(
                        task.Tags
                    )
                        ? task.Tags
                        : typeof task.Tags ===
                            "string"
                            ? [task.Tags]
                            : [];

                const subtasks =
                    Array.isArray(
                        task.subtasks
                    )
                        ? task.subtasks
                        : Array.isArray(
                            task.subtask
                        )
                            ? task.subtask
                            : Array.isArray(
                                task.SubtasksJson
                            )
                                ? task.SubtasksJson
                                : typeof task.SubtasksJson ===
                                    "string"
                                    ? (() => {
                                        try {
                                            const parsed =
                                                JSON.parse(
                                                    task.SubtasksJson
                                                );

                                            return Array.isArray(
                                                parsed
                                            )
                                                ? parsed
                                                : [];
                                        } catch {
                                            return [];
                                        }
                                    })()
                                    : [];

                const comments =
                    Array.isArray(
                        task.comments
                    )
                        ? task.comments
                        : Array.isArray(
                            task.CommentsJson
                        )
                            ? task.CommentsJson
                            : typeof task.CommentsJson ===
                                "string"
                                ? (() => {
                                    try {
                                        const parsed =
                                            JSON.parse(
                                                task.CommentsJson
                                            );

                                        return Array.isArray(
                                            parsed
                                        )
                                            ? parsed
                                            : [];
                                    } catch {
                                        return [];
                                    }
                                })()
                                : [];

                const linkedValues =
                    [
                        task.linkedLabs,
                        task.LinkedLabsJson,
                        task.linkedMedications,
                        task.LinkedMedicationsJson,
                        task.linkedImagings,
                        task.LinkedImagingsJson,
                    ].flatMap(
                        (value) => {
                            if (
                                Array.isArray(
                                    value
                                )
                            ) {
                                return value;
                            }

                            if (
                                typeof value ===
                                "string"
                            ) {
                                try {
                                    const parsed =
                                        JSON.parse(
                                            value
                                        );

                                    return Array.isArray(
                                        parsed
                                    )
                                        ? parsed
                                        : [parsed];
                                } catch {
                                    return value
                                        ? [value]
                                        : [];
                                }
                            }

                            return value
                                ? [value]
                                : [];
                        }
                    );

                const dueRaw =
                    task.dueDate ||
                    task.DueDate ||
                    task.Due ||
                    task.due ||
                    null;

                const formattedDue =
                    formatValueToMMDDYYYY(
                        dueRaw
                    );

                const textContent =
                    [
                        task.title,
                        task.Title,
                        task.Summary,
                        task.summary,
                        task.Description,
                        task.description,
                        task.Status,
                        task.status,
                        task.Priority,
                        task.priority,
                        formattedDue,
                        ...assignees,
                        ...tags,

                        ...subtasks.flatMap(
                            (subtask) => [
                                subtask?.text,
                                subtask?.Title,
                                subtask?.title,
                                subtask?.status,
                                subtask?.Status,
                                subtask?.dueDate,
                                subtask?.DueDate,

                                ...normalizeTaskAssignees(
                                    subtask?.assignee ||
                                    subtask?.Assignee
                                ),
                            ]
                        ),

                        ...comments.flatMap(
                            (comment) => [
                                comment?.text,
                                comment?.Text,
                                comment?.createdBy,
                                comment?.CreatedBy,
                            ]
                        ),

                        ...linkedValues.flatMap(
                            (item) => [
                                item?.label,
                                item?.Label,
                                item?.name,
                                item?.Name,
                                typeof item ===
                                    "string"
                                    ? item
                                    : "",
                            ]
                        ),
                    ]
                        .filter(Boolean)
                        .map((value) =>
                            String(value)
                                .trim()
                        )
                        .join(" ")
                        .toLowerCase();

                return searchTerms.every(
                    (term) =>
                        textContent.includes(
                            term
                        )
                );
            });
        }, [
            boardData?.tasks,
            filters.search,
            filters.assignee,
        ]);


    /* =====================================================
       KANBAN TASK NORMALIZATION
    ===================================================== */

    const kanbanTasks =
        useMemo(() => {
            if (
                !Array.isArray(
                    filteredTasks
                )
            ) {
                return [];
            }

            return filteredTasks.map(
                (task) => {
                    const isArchivedTask =
                        Boolean(
                            task?.isArchived ||
                            task?.IsArchived ||
                            String(
                                task?.status ||
                                task?.Status ||
                                ""
                            )
                                .trim()
                                .toLowerCase() ===
                            "archived"
                        );

                    const matchedColumn =
                        (
                            isArchivedTask
                                ? normalizedColumns.find(
                                    (c) => {
                                        const normalized =
                                            String(
                                                c?.headerText ||
                                                c?.keyField ||
                                                ""
                                            )
                                                .trim()
                                                .toLowerCase()
                                                .replace(
                                                    /[^a-z0-9]/g,
                                                    ""
                                                );

                                        return (
                                            normalized ===
                                            "archived" ||
                                            normalized ===
                                            "archive" ||
                                            normalized ===
                                            "achived" ||
                                            normalized ===
                                            "achive"
                                        );
                                    }
                                )
                                : findKanbanStatusColumn(
                                    normalizedColumns,
                                    task.statusId,
                                    task.status
                                )
                        ) ||
                        normalizedColumns[0];

                    const taskId =
                        String(
                            task.TaskId ??
                            task.Id ??
                            task.ID ??
                            task.taskId ??
                            task.id ??
                            ""
                        );

                    const title =
                        task.Title ||
                        task.title ||
                        task.Summary ||
                        task.summary ||
                        (
                            taskId
                                ? `Task #${taskId}`
                                : "Untitled"
                        );

                    const description =
                        task.Description ||
                        task.description ||
                        "";

                    const statusId =
                        matchedColumn?.keyField ??
                        task.statusId ??
                        task.StatusId ??
                        "";

                    const status =
                        matchedColumn?.headerText ||
                        task.status ||
                        task.Status ||
                        "";

                    const priority =
                        task.Priority ||
                        task.priority ||
                        "";

                    const dueDate =
                        task.DueDate ||
                        task.dueDate ||
                        "";

                    return {
                        ...task,

                        Id: taskId,
                        TaskId: taskId,
                        id: taskId,

                        Title: title,
                        title,

                        Description:
                            description,
                        description,

                        StatusId:
                            statusId,
                        statusId,

                        Status: status,
                        status,

                        IsArchived:
                            isArchivedTask,
                        isArchived:
                            isArchivedTask,

                        Priority:
                            priority,
                        priority,

                        DueDate:
                            dueDate,
                        dueDate,

                        rawTask:
                            task,
                    };
                }
            );
        }, [
            filteredTasks,
            normalizedColumns,
        ]);


    /* =====================================================
       GROUP TASKS BY COLUMN
    ===================================================== */

    const tasksByColumn =
        useMemo(() => {
            const grouped = {};

            normalizedColumns.forEach(
                (column) => {
                    grouped[
                        String(
                            column.keyField
                        )
                    ] = [];
                }
            );

            kanbanTasks.forEach(
                (task) => {
                    const matchedColumn =
                        findKanbanStatusColumn(
                            normalizedColumns,
                            task.statusId,
                            task.status
                        );

                    const columnKey =
                        String(
                            matchedColumn?.keyField ??
                            task.statusId ??
                            normalizedColumns[0]
                                ?.keyField ??
                            ""
                        );

                    if (
                        !grouped[columnKey]
                    ) {
                        grouped[
                            columnKey
                        ] = [];
                    }

                    grouped[
                        columnKey
                    ].push(task);
                }
            );

            return grouped;
        }, [
            kanbanTasks,
            normalizedColumns,
        ]);


    /* =====================================================
       RESTORE / REFRESH
    ===================================================== */

    const restoreKanbanView =
        useCallback(() => {
            setBoardData((prev) => {
                if (!prev)
                    return prev;

                return {
                    ...prev,
                    tasks: (
                        prev.tasks || []
                    ).map((task) => ({
                        ...task,
                    })),
                };
            });

            setActiveDragTask(null);
        }, []);


    /* =====================================================
       DELETE
    ===================================================== */

    const openDeleteConfirm = (
        task
    ) => {
        if (!task) return;

        restoreKanbanView();

        setDeleteTaskId(
            task.id ??
            task.Id ??
            task.TaskId
        );

        setDeleteTaskTitle(
            task.title ||
            task.Title ||
            ""
        );

        setDeleteVisible(true);
    };


    const resetDeleteConfirm =
        () => {
            setDeleteVisible(
                false
            );

            setDeleteTaskId(null);

            setDeleteTaskTitle("");

            setIsDeletingTask(false);
        };


    const closeDeleteConfirm =
        () => {
            resetDeleteConfirm();

            restoreKanbanView();
        };


    const deleteTask =
        async (id) => {
            const response =
                await SaveTasks({
                    action: "DELETE",
                    taskId: id,
                    boardId,
                });

            if (response?.error) {
                console.error(
                    "Failed to delete task:",
                    response.error
                );

                return false;
            }

            await fetchBoard();

            return true;
        };


    const handleDelete =
        async () => {
            if (
                deleteTaskId ===
                null ||
                deleteTaskId ===
                undefined ||
                isDeletingTask
            ) {
                return;
            }

            setIsDeletingTask(
                true
            );

            const deleted =
                await deleteTask(
                    deleteTaskId
                );

            if (deleted) {
                resetDeleteConfirm();
            } else {
                setIsDeletingTask(
                    false
                );
            }
        };


    /* =====================================================
       UPDATE STATUS
    ===================================================== */

    const applyTaskStatus =
        async (
            taskId,
            statusId,
            status,
            originalTask
        ) => {
            const taskToSave =
                originalTask ||
                boardData?.tasks?.find(
                    (task) =>
                        String(
                            task.id
                        ) ===
                        String(
                            taskId
                        )
                ) ||
                {};

            const stringifyJsonField =
                (
                    jsonValue,
                    parsedValue,
                    fallback = []
                ) => {
                    if (
                        jsonValue !==
                        null &&
                        jsonValue !==
                        undefined
                    ) {
                        return typeof jsonValue ===
                            "string"
                            ? jsonValue
                            : JSON.stringify(
                                jsonValue
                            );
                    }

                    return JSON.stringify(
                        parsedValue ??
                        fallback
                    );
                };


            /* Optimistic update */
            setBoardData((prev) =>
                prev
                    ? {
                        ...prev,

                        tasks: (
                            prev.tasks ||
                            []
                        ).map(
                            (task) =>
                                String(
                                    task.id
                                ) ===
                                    String(
                                        taskId
                                    )
                                    ? {
                                        ...task,

                                        statusId,
                                        status,

                                        StatusId:
                                            statusId,

                                        Status:
                                            status,
                                    }
                                    : task
                        ),
                    }
                    : prev
            );


            const response =
                await SaveTasks({
                    action: "UPDATE",

                    taskId,

                    boardId:
                        taskToSave.BoardId ??
                        taskToSave.boardId ??
                        boardId,

                    taskType:
                        taskToSave.TaskType ??
                        taskToSave.taskType ??
                        "",

                    title:
                        taskToSave.Title ??
                        taskToSave.title ??
                        "",

                    description:
                        taskToSave.Description ??
                        taskToSave.description ??
                        "",

                    statusId,
                    status,

                    priority:
                        taskToSave.Priority ??
                        taskToSave.priority ??
                        "",

                    dueDate:
                        taskToSave.DueDate ??
                        taskToSave.dueDate ??
                        "",

                    reminderDate:
                        taskToSave.ReminderDate ??
                        taskToSave.reminderDate ??
                        "",

                    createdBy:
                        taskToSave.CreatedBy ??
                        taskToSave.createdBy ??
                        "",

                    linkedLabsJson:
                        stringifyJsonField(
                            taskToSave.LinkedLabsJson,
                            taskToSave.linkedLabs
                        ),

                    linkedMedicationsJson:
                        stringifyJsonField(
                            taskToSave.LinkedMedicationsJson,
                            taskToSave.linkedMedications
                        ),

                    linkedImagingsJson:
                        stringifyJsonField(
                            taskToSave.LinkedImagingsJson,
                            taskToSave.linkedImagings
                        ),

                    tagsJson:
                        stringifyJsonField(
                            taskToSave.TagsJson,
                            taskToSave.tags ??
                            taskToSave.Tags
                        ),

                    commentsJson:
                        stringifyJsonField(
                            taskToSave.CommentsJson,
                            taskToSave.comments
                        ),

                    activityLogsJson:
                        stringifyJsonField(
                            taskToSave.ActivityLogsJson,
                            taskToSave.activities
                        ),

                    watchersJson:
                        stringifyJsonField(
                            taskToSave.WatchersJson,
                            taskToSave.watchers
                        ),

                    assignedUsersJson:
                        stringifyJsonField(
                            taskToSave.AssignedUsersJson,
                            taskToSave.assignedUsers
                        ),

                    subtasksJson:
                        stringifyJsonField(
                            taskToSave.SubtasksJson,
                            taskToSave.subtasks ??
                            taskToSave.subtask
                        ),

                    attachmentsJson:
                        stringifyJsonField(
                            taskToSave.AttachmentsJson ??
                            taskToSave.FilesJson,
                            taskToSave.files ??
                            taskToSave.attachments
                        ),
                });


            if (response?.error) {
                await fetchBoard();

                return false;
            }

            return true;
        };


    /* =====================================================
       ARCHIVE
    ===================================================== */

    const resetArchiveConfirm =
        () => {
            setArchiveConfirmVisible(
                false
            );

            setArchiveCandidate(
                null
            );
        };


    const closeArchiveConfirm =
        () => {
            resetArchiveConfirm();

            restoreKanbanView();
        };


    const handleConfirmArchive =
        async () => {
            if (
                !archiveCandidate
            ) {
                return;
            }

            const archived =
                await applyTaskStatus(
                    archiveCandidate.taskId,
                    archiveCandidate.statusId,
                    archiveCandidate.status
                );

            if (archived) {
                resetArchiveConfirm();
            }
        };


    /* =====================================================
       FETCH BOARD
    ===================================================== */

    const fetchBoard =
        async (showLoader = false) => {
            if (showLoader) {
                setIsBoardLoading(true);
            }

            try {
                const response =
                    await SaveBoard(
                        "GET",
                        null,
                        boardId
                    );

                const parsed =
                    typeof response?.d ===
                        "string"
                        ? JSON.parse(
                            response.d
                        )
                        : response;

                const rawData =
                    parsed?.data;

                if (
                    !Array.isArray(
                        rawData
                    ) ||
                    rawData.length < 3
                ) {
                    console.error(
                        "Invalid board payload shape:",
                        rawData
                    );

                    setBoardData(
                        null
                    );

                    return;
                }


                const [
                    boardRows = [],
                    statusRows = [],
                    memberRows = [],
                ] = rawData;


                const boardInfo =
                    Array.isArray(
                        boardRows
                    )
                        ? boardRows[0]
                        : boardRows;


                /* =========================================
                   NORMALIZE COLUMNS
                ========================================= */

                const columns =
                    statusRows.map(
                        (status) => ({
                            ...status,

                            keyField:
                                String(
                                    status.StatusId ??
                                    status.StatusID ??
                                    status.statusId ??
                                    status.id
                                ),

                            headerText:
                                status.StatusName ??
                                status.Status ??
                                status.name ??
                                "Untitled",
                        })
                    );


                /* =========================================
                   MEMBERS
                ========================================= */

                const members =
                    memberRows.map(
                        (member) => ({
                            value:
                                member?.ref_id,

                            label:
                                member?.name ||
                                member?.email ||
                                "",

                            ref_id:
                                member?.ref_id,

                            name:
                                member?.name,

                            email:
                                member?.email,

                            role:
                                member?.role,
                        })
                    );


                /* =========================================
                   FETCH TASKS
                ========================================= */

                let taskRows = [];

                try {
                    const response1 =
                        await SaveTasks({
                            action:
                                "GET_BY_BOARDID",
                            boardId,
                        });

                    taskRows =
                        parseTaskRows(
                            response1
                        );
                } catch (error) {
                    console.error(
                        "GET_BY_BOARDID failed for TempKanban, falling back to user-scoped task fetch:",
                        error
                    );
                }


                /* =========================================
                   FALLBACK TASK FETCH
                ========================================= */

                if (
                    !taskRows.length
                ) {
                    const {
                        userId,
                        username,
                    } =
                        getCurrentUserDetails();

                    const lookupValues =
                        Array.from(
                            new Set(
                                [
                                    String(
                                        userId ||
                                        ""
                                    ).trim(),

                                    String(
                                        username ||
                                        ""
                                    ).trim(),
                                ].filter(
                                    Boolean
                                )
                            )
                        );

                    const fallbackResponses =
                        await Promise.all(
                            lookupValues.flatMap(
                                (
                                    lookupValue
                                ) => {
                                    const assignedUsers =
                                        [
                                            {
                                                UserId:
                                                    userId ||
                                                    lookupValue,

                                                Username:
                                                    username ||
                                                    lookupValue,

                                                value:
                                                    userId ||
                                                    lookupValue,

                                                label:
                                                    username ||
                                                    lookupValue,
                                            },
                                        ];

                                    return [
                                        SaveTasks(
                                            {
                                                action:
                                                    "GET_BY_ASSIGNEDTOME",

                                                createdBy:
                                                    lookupValue,

                                                assignedUsers,
                                            }
                                        ),

                                        SaveTasks(
                                            {
                                                action:
                                                    "GET_CREATEDBYME",

                                                createdBy:
                                                    lookupValue,
                                            }
                                        ),
                                    ];
                                }
                            )
                        );

                    const mergedRows =
                        fallbackResponses.flatMap(
                            (response) =>
                                parseTaskRows(
                                    response
                                )
                        );

                    const seenTaskIds =
                        new Set();

                    taskRows =
                        mergedRows.filter(
                            (task) => {
                                const taskBoardId =
                                    String(
                                        task?.boardId ??
                                        task?.BoardId ??
                                        task?.boardID ??
                                        task?.BoardID ??
                                        ""
                                    );

                                const taskId =
                                    String(
                                        task?.TaskId ??
                                        task?.taskId ??
                                        task?.Id ??
                                        task?.id ??
                                        ""
                                    );

                                if (
                                    taskBoardId !==
                                    String(
                                        boardId
                                    ) ||
                                    !taskId ||
                                    seenTaskIds.has(
                                        taskId
                                    )
                                ) {
                                    return false;
                                }

                                seenTaskIds.add(
                                    taskId
                                );

                                return true;
                            }
                        );
                }


                console.log(
                    "Board tasks:",
                    taskRows
                );


                /* =========================================
                   SAFE JSON PARSE
                ========================================= */

                const safeParse =
                    (
                        value,
                        fallback = []
                    ) => {
                        if (!value)
                            return fallback;

                        try {
                            const parsed =
                                typeof value ===
                                    "string"
                                    ? JSON.parse(
                                        value
                                    )
                                    : value;

                            return Array.isArray(
                                parsed
                            )
                                ? parsed
                                : parsed ??
                                fallback;
                        } catch {
                            return fallback;
                        }
                    };


                /* =========================================
                   NORMALIZE TASKS
                ========================================= */

                const tasks =
                    taskRows.map(
                        (task) => {
                            const matchedColumn =
                                findKanbanStatusColumn(
                                    columns,
                                    task.statusId ??
                                    task.StatusId,
                                    task.status ??
                                    task.Status
                                );

                            const normalizedId =
                                String(
                                    task.id ??
                                    task.ID ??
                                    task.TaskId ??
                                    ""
                                );

                            const normalizedStatusId =
                                String(
                                    matchedColumn?.keyField ??
                                    task.statusId ??
                                    task.StatusId ??
                                    ""
                                );

                            const normalizedStatus =
                                matchedColumn?.headerText ??
                                task.status ??
                                task.Status ??
                                "";

                            const normalizedTitle =
                                task.Title ??
                                task.title ??
                                task.Summary ??
                                task.summary ??
                                (
                                    normalizedId
                                        ? `Task #${normalizedId}`
                                        : "Untitled"
                                );

                            const normalizedDescription =
                                task.Description ??
                                task.description ??
                                "";


                            return {
                                ...task,

                                Id:
                                    normalizedId,

                                TaskId:
                                    normalizedId,

                                id:
                                    normalizedId,

                                StatusId:
                                    normalizedStatusId,

                                statusId:
                                    normalizedStatusId,

                                Status:
                                    normalizedStatus,

                                status:
                                    normalizedStatus,

                                Title:
                                    normalizedTitle,

                                title:
                                    normalizedTitle,

                                Description:
                                    normalizedDescription,

                                description:
                                    normalizedDescription,

                                subtasks:
                                    safeParse(
                                        task.SubtasksJson
                                    ),

                                assignedUsers:
                                    safeParse(
                                        task.AssignedUsersJson
                                    ),

                                watchers:
                                    safeParse(
                                        task.WatchersJson
                                    ),

                                comments:
                                    safeParse(
                                        task.CommentsJson
                                    ),

                                activities:
                                    safeParse(
                                        task.ActivityLogsJson
                                    ),

                                tags:
                                    safeParse(
                                        task.TagsJson
                                    ),

                                linkedLabs:
                                    safeParse(
                                        task.LinkedLabsJson
                                    ),

                                linkedMedications:
                                    safeParse(
                                        task.LinkedMedicationsJson
                                    ),

                                linkedImagings:
                                    safeParse(
                                        task.LinkedImagingsJson
                                    ),
                            };
                        }
                    );


                /* =========================================
                   FALLBACK COLUMNS
                ========================================= */

                const existingColumnKeys =
                    new Set(
                        columns.map(
                            (col) =>
                                String(
                                    col.keyField
                                )
                        )
                    );

                const augmentedColumns =
                    [...columns];


                tasks.forEach(
                    (task) => {
                        const matchedColumn =
                            findKanbanStatusColumn(
                                augmentedColumns,
                                task.statusId,
                                task.status
                            );

                        const statusKey =
                            String(
                                task.statusId ||
                                ""
                            ).trim();

                        const statusLabel =
                            String(
                                task.status ||
                                task.Status ||
                                ""
                            ).trim();

                        if (
                            !statusLabel ||
                            matchedColumn
                        ) {
                            return;
                        }

                        const fallbackKey =
                            statusKey ||
                            statusLabel;

                        if (
                            !existingColumnKeys.has(
                                fallbackKey
                            )
                        ) {
                            existingColumnKeys.add(
                                fallbackKey
                            );

                            augmentedColumns.push(
                                {
                                    keyField:
                                        fallbackKey,

                                    headerText:
                                        statusLabel ||
                                        `Status ${fallbackKey}`,

                                    isFallback:
                                        true,
                                }
                            );
                        }
                    }
                );


                /* =========================================
                   FINAL BOARD
                ========================================= */

                const finalBoardData = {
                    ...boardInfo,

                    columns:
                        augmentedColumns,

                    tasks,

                    refs:
                        taskRows,

                    members,
                };


                console.log(
                    "MAPPED TASKS:",
                    tasks
                );

                console.log(
                    "COLUMNS:",
                    augmentedColumns
                );

                console.log(
                    "MEMBERS:",
                    members
                );


                setBoardData(
                    finalBoardData
                );

                console.log(
                    "Fetched board data:",
                    finalBoardData
                );
            } catch (error) {
                console.error(
                    "Fetch board error:",
                    error
                );
            } finally {
                if (showLoader) {
                    setIsBoardLoading(false);
                }
            }
        };


    /* =====================================================
       LOAD BOARD
    ===================================================== */

    useEffect(() => {
        if (!boardId) {
            setIsBoardLoading(false);
            return;
        }

        fetchBoard(true);
    }, [boardId]);

    useEffect(() => {
        if (!boardId) {
            return;
        }

        const wasOpen =
            prevUpdateTaskVisibleRef.current;

        if (wasOpen && !updateTaskVisible) {
            fetchBoard();
        }

        prevUpdateTaskVisibleRef.current =
            updateTaskVisible;
    }, [boardId, updateTaskVisible]);


    /* =====================================================
       DRAG START
    ===================================================== */

    const handleDragStart =
        (event) => {
            const task =
                event?.active?.data
                    ?.current?.task;

            if (!task) return;

            const isArchivedTask =
                Boolean(
                    task?.isArchived ||
                    task?.IsArchived ||
                    isArchiveStatus(
                        task?.status ||
                        task?.Status ||
                        task?.statusId ||
                        task?.StatusId ||
                        ""
                    )
                );

            if (
                isArchivedTask
            ) {
                return;
            }

            setActiveDragTask(
                task
            );
        };


    /* =====================================================
       DRAG CANCEL
    ===================================================== */

    const handleDragCancel =
        () => {
            setActiveDragTask(
                null
            );
        };


    /* =====================================================
       DRAG END
    ===================================================== */

    const handleDragEnd =
        async (event) => {
            const {
                active,
                over,
            } = event;

            setActiveDragTask(
                null
            );

            if (
                !active ||
                !over
            ) {
                return;
            }

            const draggedTask =
                active?.data
                    ?.current?.task;

            if (
                !draggedTask
            ) {
                return;
            }


            /* =============================================
               FIND DROP COLUMN
            ============================================= */

            let targetColumn = null;

            const overData =
                over?.data?.current;

            if (
                overData?.type ===
                "column"
            ) {
                targetColumn =
                    overData.column ||
                    normalizedColumns.find(
                        (column) =>
                            String(
                                column.keyField
                            ) ===
                            String(
                                overData.statusId
                            )
                    );
            }


            if (
                !targetColumn
            ) {
                const overId =
                    String(
                        over.id || ""
                    );

                const columnId =
                    overId.startsWith(
                        "column-"
                    )
                        ? overId.replace(
                            "column-",
                            ""
                        )
                        : overId;

                targetColumn =
                    normalizedColumns.find(
                        (column) =>
                            String(
                                column.keyField
                            ) ===
                            String(
                                columnId
                            )
                    );
            }


            if (
                !targetColumn
            ) {
                restoreKanbanView();

                return;
            }


            /* =============================================
               ORIGINAL TASK
            ============================================= */

            const originalTask =
                boardData?.tasks?.find(
                    (task) =>
                        String(
                            task.id
                        ) ===
                        String(
                            draggedTask.id
                        )
                );


            if (
                !originalTask
            ) {
                return;
            }


            const newStatusId =
                String(
                    targetColumn.keyField
                );

            const newStatus =
                targetColumn.headerText ||
                "";


            const oldStatusId =
                String(
                    originalTask?.statusId ??
                    originalTask?.StatusId ??
                    ""
                );


            /* =============================================
               SAME COLUMN
            ============================================= */

            if (
                oldStatusId ===
                newStatusId
            ) {
                return;
            }


            const subtasks = originalTask.subtasks || originalTask.subtask || originalTask.subTasks || originalTask.SubTasks || [];
            if (['done', 'completed'].includes(newStatus.trim().toLowerCase()) && hasIncompleteTaskSubtasks(subtasks)) {
                toast.error('All Subtasks Need To Be Completed.', toastConfig);
                restoreKanbanView();
                return;
            }

            /* =============================================
               ARCHIVE
            ============================================= */

            if (
                isArchiveStatus(
                    targetColumn
                )
            ) {
                setArchiveCandidate(
                    {
                        taskId:
                            draggedTask.id,

                        title:
                            draggedTask.title ||
                            originalTask?.title ||
                            originalTask?.Title ||
                            "",

                        statusId:
                            newStatusId,

                        status:
                            newStatus,
                    }
                );

                setArchiveConfirmVisible(
                    true
                );

                return;
            }


            /* =============================================
               UPDATE STATUS
            ============================================= */

            const updated =
                await applyTaskStatus(
                    draggedTask.id,
                    newStatusId,
                    newStatus,
                    originalTask
                );


            if (!updated) {
                await fetchBoard();
            }
        };


    /* =====================================================
       RENDER
    ===================================================== */

    if (isBoardLoading) {
        return (
            <div className="task-board-theme dashboard-dark-theme min-vh-100 d-flex flex-column align-items-center justify-content-center gap-3">
                <CSpinner color="primary" aria-label="Loading board" />
                <span>Loading board...</span>
            </div>
        );
    }

    return (
        <div
            ref={
                boardContainerRef
            }
            className="task-board-theme dashboard-dark-theme p-4 min-vh-100 tempkanban-container"
        >

            {/* =================================================
                RESPONSIVE CSS
            ================================================= */}

            <style>
                {`
                    .tempkanban-back-btn svg {
                        width: 22px !important;
                        height: 22px !important;
                    }

                    .tempkanban-controls-inner .tempkanban-back-btn, 
                    .tempkanban-controls-inner .tempkanban-create-btn, 
                    .tempkanban-controls-inner .tempkanban-search-wrap, 
                    .tempkanban-controls-inner .task-board-filter-btn {
                        height: 42px !important; min-height: 42px !important;
                    }
                    .tempkanban-controls-inner .tempkanban-back-btn {
                        width: 42px !important; min-width: 42px !important;
                    }

                    .tempkanban-board-scroll {
                        scrollbar-width: thin;
                    }

                  .tempkanban-columns {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: stretch;
  gap: 14px;
  width: 100%;
  min-height: calc(100vh - 180px);
}

                    .tempkanban-draggable-wrapper {
                        width: 100%;
                    }

                    .tempkanban-draggable-wrapper:active {
                        cursor: grabbing !important;
                    }

                    @media (max-width: 640px) {

                        .tempkanban-controls-inner {
                            flex-wrap: wrap !important;
                            width: 100%;
                        }

                        .tempkanban-search-filter-row {
                            width: 100%;
                            display: flex;
                            flex-wrap: nowrap !important;
                            min-width: 0 !important;
                            gap: 8px !important;
                            align-items: center;
                        }

                        .tempkanban-search-wrap {
                            flex: 1;
                            min-width: 0 !important;
                            width: auto !important;
                        }

                        .tempkanban-search-wrap input {
                            width: 100%;
                        }

                        .tempkanban-filter-text {
                            display: none;
                        }

                        .tempkanban-filter-btn {
                            padding: 8px !important;
                            min-width: 42px !important;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                        }

                        .tempkanban-create-btn {
                            font-size: 13px !important;
                            padding: 6px 14px !important;
                            min-height: 40px;
                            font-weight: 500 !important;
                        }

                        .tempkanban-back-btn {
                            min-width: 40px !important;
                            min-height: 40px !important;
                            width: 40px !important;
                            height: 40px !important;
                        }

                        .tempkanban-column {
                            min-width: 240px !important;
                            width: 240px !important;
                        }
                    }
                `}
            </style>


            {/* =================================================
                BOARD TITLE
            ================================================= */}

            <div
                style={{
                    marginBottom:
                        "10px",
                    margin:
                        "0px 12px",
                }}
                className="d-flex flex-column gap-2"
            >
                <p
                    className="p-0 m-0"
                    style={{
                        color:
                            "var(--ui-text-primary)",
                        fontWeight: 500,
                        letterSpacing:
                            "-0.4px",
                        lineHeight:
                            "20px",
                        fontSize:
                            "23px",
                    }}
                >
                    {boardData?.name}
                </p>

                <p
                    className="p-0 m-0"
                    style={{
                        color:
                            "var(--ui-text-muted)",
                        letterSpacing:
                            "-0.4px",
                        lineHeight:
                            "20px",
                        fontSize:
                            "13px",
                    }}
                >
                    {
                        boardData?.description
                    }
                </p>
            </div>


            {/* =================================================
                CONTROLS
            ================================================= */}

            <div
                style={{
                    margin:
                        "10px 12px",
                }}
                className="d-flex justify-content-between align-items-start flex-wrap gap-3 tempkanban-controls-container"
            >

                <div
                    className="d-flex gap-2 justify-content-between align-items-center tempkanban-controls-inner"
                >

                    {/* BACK */}
                    <CButton
                        variant="outline"
                        color="primary"
                        onClick={() => {
                            window.location.href =
                                "/home/all-tasks";
                        }}
                        className="task-board-icon-btn d-flex align-items-center justify-content-center tempkanban-back-btn flex-shrink-0"
                        style={{
                            minWidth:
                                "45px",
                            minHeight:
                                "45px",
                            width:
                                "45px",
                            height:
                                "45px",
                            padding: 0,
                            flexShrink:
                                0,
                        }}
                    >
                        <ArrowLeft
                            size={22}
                            style={{
                                flexShrink:
                                    0,
                                minWidth:
                                    "22px",
                            }}
                        />
                    </CButton>


                    {/* CREATE TASK */}
                    <CreateTask
                        visible={
                            createTaskVisible
                        }
                        fetchBoard={
                            fetchBoard
                        }
                        setVisible={
                            setCreateTaskVisible
                        }
                        className="tempkanban-create-btn"
                    />


                    {/* SEARCH + FILTER */}
                    <div className="d-flex align-items-center gap-2 flex-nowrap tempkanban-search-filter-row">

                        {/* SEARCH */}
                        <div
                            className="tempkanban-search-wrap"
                            style={{
                                position:
                                    "relative",
                                minWidth:
                                    "220px",
                                width:
                                    "220px",
                            }}
                        >
                            <Search
                                size={16}
                                style={{
                                    position:
                                        "absolute",
                                    left:
                                        "12px",
                                    top:
                                        "50%",
                                    transform:
                                        "translateY(-50%)",
                                    color:
                                        "#667085",
                                    zIndex:
                                        2,
                                }}
                            />

                            <CFormInput
                                placeholder="Search Tasks"
                                value={
                                    filters.search
                                }
                                onChange={(
                                    e
                                ) => {
                                    setFilters(
                                        (
                                            prev
                                        ) => ({
                                            ...prev,
                                            search:
                                                e
                                                    .target
                                                    .value,
                                        })
                                    );
                                }}
                                className="task-board-search-input"
                                style={{
                                    paddingLeft:
                                        "40px",
                                    minHeight:
                                        "42px",
                                    borderRadius:
                                        "12px",
                                    border:
                                        "1px solid var(--ui-dashboard-border-strong)",
                                }}
                            />
                        </div>


                        {/* ASSIGNEE CHIPS */}
                        {selectedAssignees.map((item) => (
                            <div key={item.value} style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "6px 12px 6px 8px", borderRadius: "999px", background: "var(--ui-dashboard-surface-2)", border: "1px solid var(--ui-dashboard-border)", whiteSpace: "nowrap" }}>
                                <div style={{ width: "26px", height: "26px", borderRadius: "50%", background: "var(--ui-dashboard-surface-3)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "11px" }}>
                                    {String(item.label).slice(0, 2).toUpperCase()}
                                </div>
                                <span style={{ fontSize: "14px", fontWeight: 600 }}>{item.label}</span>
                                <X size={14} style={{ cursor: "pointer", opacity: 0.7 }} onClick={(e) => { e.stopPropagation(); setFilters((prev) => ({ ...prev, assignee: (Array.isArray(prev.assignee) ? prev.assignee : [prev.assignee]).filter((v) => v !== item.value) })); }} />
                            </div>
                        ))}


                        {/* FILTER */}
                        <div
                            ref={filterRef}
                            style={{
                                position:
                                    "relative",
                            }}
                        >
                            <CButton
                                color="primary"
                                variant="outline"
                                className="kanban-filter-trigger task-board-filter-btn tempkanban-filter-btn"
                                style={{
                                    minHeight:
                                        "39px",
                                    borderRadius:
                                        "9px",
                                    padding:
                                        "7px 16px",
                                    display:
                                        "inline-flex",
                                    alignItems:
                                        "center",
                                    gap:
                                        "8px",
                                }}
                                onClick={() =>
                                    setShowFilterDropdown(
                                        (
                                            prev
                                        ) =>
                                            !prev
                                    )
                                }
                            >
                                <HRMFilterIcon
                                    style={{
                                        fontSize:
                                            "16px",
                                    }}
                                />

                                <span className="tempkanban-filter-text">
                                    Filter
                                </span>
                            </CButton>


                            {showFilterDropdown && (
                                <div
                                    className="dashboard-dark-filter-popover"
                                    style={{
                                        position:
                                            "absolute",
                                        right:
                                            0,
                                        top:
                                            "calc(100% + 10px)",
                                        width:
                                            "300px",
                                        background:
                                            "var(--ui-bg-filter)",
                                        borderRadius:
                                            "16px",
                                        boxShadow:
                                            "none",
                                        padding:
                                            "16px",
                                        zIndex:
                                            50,
                                    }}
                                >
                                    <div
                                        style={{
                                            marginBottom:
                                                "12px",
                                            fontWeight:
                                                700,
                                            fontSize:
                                                "14px",
                                        }}
                                    >
                                        Filter tasks
                                    </div>

                                    <CFormLabel
                                        style={{
                                            fontSize:
                                                "13px",
                                            marginBottom:
                                                "8px",
                                            display:
                                                "block",
                                        }}
                                    >
                                        Assignee
                                    </CFormLabel>

                                    <Select
                                        aria-label="Assignee filter"
                                        isMulti
                                        value={
                                            selectedAssignees
                                        }
                                        options={
                                            assigneeFilterOptions
                                        }
                                        styles={
                                            assigneeSelectStyles
                                        }
                                        isSearchable={
                                            false
                                        }
                                        onChange={(
                                            options
                                        ) => {
                                            setFilters(
                                                (
                                                    prev
                                                ) => ({
                                                    ...prev,
                                                    assignee:
                                                        (options || []).map((o) => o.value),
                                                })
                                            );
                                        }}
                                    />

                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                            marginTop: "14px",
                                        }}
                                    >
                                        <CButton
                                            color="secondary"
                                            variant="outline"
                                            style={{
                                                flex: 1,
                                                borderRadius: "10px",
                                                fontSize: "13px",
                                                padding: "7px 10px",
                                                whiteSpace: "nowrap",
                                            }}
                                            onClick={() => {
                                                setFilters(
                                                    (
                                                        prev
                                                    ) => ({
                                                        ...prev,
                                                        assignee:
                                                            [],
                                                    })
                                                );
                                            }}
                                        >
                                            Clear filters
                                        </CButton>

                                        <CButton
                                            color="primary"
                                            className="kanban-filter-apply"
                                            style={{
                                                flex: 1,
                                                borderRadius: "10px",
                                                fontSize: "13px",
                                                padding: "7px 10px",
                                                whiteSpace: "nowrap",
                                            }}
                                            onClick={() =>
                                                setShowFilterDropdown(
                                                    false
                                                )
                                            }
                                        >
                                            Apply
                                        </CButton>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>


                {/* BOARD SETTINGS */}
                <div className="tempkanban-board-settings-slot">
                    <DeleteBoard
                        fetchBoard={
                            fetchBoard
                        }
                        boardData={
                            boardData
                        }
                    />
                </div>
            </div>


            {/* =================================================
                UPDATE TASK
            ================================================= */}

            <UpdateTask
                visible={
                    updateTaskVisible
                }
                fetchBoard={
                    fetchBoard
                }
                updateTaskId={
                    updateTaskId
                }
                setUpdateTaskId={
                    setUpdateTaskId
                }
                setVisible={
                    setUpdateTaskVisible
                }
                boardData={
                    boardData
                }
                breadcrumbPrevText="All Tasks"
                breadcrumbPrevHref="/home/all-tasks"
            />


            {/* =================================================
                DND KANBAN
            ================================================= */}

            <DndContext
                sensors={
                    sensors
                }
                collisionDetection={
                    closestCenter
                }
                onDragStart={
                    handleDragStart
                }
                onDragCancel={
                    handleDragCancel
                }
                onDragEnd={
                    handleDragEnd
                }
            >

                <div
                    className="kanban-mobile-scroll-wrapper tempkanban-full-width"
                    style={{
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        paddingBottom: "12px",
    }}
                >

                    <div
                        className="tempkanban-columns"
                        style={{
            width: "100%",
            minWidth: 0,
        }}
                    >

                        {normalizedColumns.map(
                            (column) => (
                                <KanbanColumn 
                                    key={
                                        column.keyField
                                    }
                                    column={
                                        column
                                    }
                                    tasks={
                                        tasksByColumn[
                                            String(
                                                column.keyField
                                            )
                                        ] || []
                                    }
                                    onOpenTask={(
                                        task
                                    ) => {
                                        setUpdateTaskId(
                                            task.rawTask ||
                                            task
                                        );

                                        setUpdateTaskVisible(
                                            true
                                        );
                                    }}
                                    onDeleteTask={
                                        openDeleteConfirm
                                    }
                                    activeTaskId={
                                        activeDragTask?.id
                                    }
                                />
                            )
                        )}

                    </div>
                </div>


                {/* =================================================
                    DRAG OVERLAY
                ================================================= */}

                {activeDragTask && (
                    <div
                        style={{
                            position:
                                "fixed",
                            left:
                                "50%",
                            top:
                                "18px",
                            transform:
                                "translateX(-50%)",
                            zIndex:
                                9999,
                            pointerEvents:
                                "none",
                            background:
                                "var(--ui-dashboard-surface-2)",
                            border:
                                `1px solid ${KANBAN_PRIMARY_BLUE}`,
                            borderRadius:
                                "10px",
                            padding:
                                "8px 14px",
                            boxShadow:
                                "0 12px 30px rgba(0,0,0,.25)",
                            color:
                                "var(--ui-text-primary)",
                            fontWeight:
                                600,
                            fontSize:
                                "13px",
                        }}
                    >
                        Moving:{" "}
                        {activeDragTask?.title ||
                            activeDragTask?.Title ||
                            "Task"}
                    </div>
                )}

            </DndContext>


            {/* =================================================
                DELETE MODAL
            ================================================= */}

            <CModal
                visible={
                    deleteVisible
                }
                backdrop="static"
                onClose={
                    closeDeleteConfirm
                }
                className="hrm-confirm-modal"
                centered
            >
                <CModalHeader
                    onClose={
                        closeDeleteConfirm
                    }
                >
                    <CModalTitle>
                        Delete Task
                    </CModalTitle>
                </CModalHeader>

                <CModalBody>
                    Are you sure you want to delete
                    {deleteTaskTitle ? (
                        <>
                            {" "}
                            <strong>
                                {
                                    deleteTaskTitle
                                }
                            </strong>
                        </>
                    ) : (
                        " this task"
                    )}
                    ?
                </CModalBody>

                <CModalFooter>
                    <CButton
                        color="secondary"
                        variant="outline"
                        onClick={
                            closeDeleteConfirm
                        }
                    >
                        Cancel
                    </CButton>

                    <CButton
                        color="danger"
                        className="hrm-modal-danger-btn"
                        onClick={
                            handleDelete
                        }
                        disabled={
                            isDeletingTask
                        }
                    >
                        {isDeletingTask
                            ? "Deleting..."
                            : "Delete"}
                    </CButton>
                </CModalFooter>
            </CModal>


            {/* =================================================
                ARCHIVE MODAL
            ================================================= */}

            <CModal
                visible={
                    archiveConfirmVisible
                }
                backdrop="static"
                onClose={
                    closeArchiveConfirm
                }
                className="hrm-confirm-modal"
                centered
            >
                <CModalHeader
                    onClose={
                        closeArchiveConfirm
                    }
                >
                    <CModalTitle>
                        Archive Task
                    </CModalTitle>
                </CModalHeader>

                <CModalBody>
                    {archiveCandidate?.title ? (
                        <>
                            Are you sure you want
                            to archive{" "}
                            <strong>
                                {
                                    archiveCandidate.title
                                }
                            </strong>
                            ?
                        </>
                    ) : (
                        "Are you sure you want to archive this task?"
                    )}

                    {" "}
                    Once archived, the status
                    can no longer be modified,
                    but it will still be
                    available for viewing.
                </CModalBody>

                <CModalFooter>
                    <CButton
                        color="secondary"
                        variant="outline"
                        onClick={
                            closeArchiveConfirm
                        }
                    >
                        Cancel
                    </CButton>

                    <CButton
                        color="danger"
                        className="hrm-modal-danger-btn"
                        onClick={
                            handleConfirmArchive
                        }
                    >
                        Archive
                    </CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
};


export default TempKanban;
